// src/store/store.js
import { createStore } from 'vuex'; // For Vue 3, use 'import Vue from "vue"; import Vuex from "vuex"' for Vue 2
import { cartApi, wishlistApi } from '@/services/cartWishlistApi';
import { toastService } from '@/services/toast';

const clone = (v) => JSON.parse(JSON.stringify(v));

// Guest cart/wishlist awaiting migration into an account. Written before a merge
// is attempted and only removed once it succeeds, so an interrupted or
// auth-failed merge (e.g. an expired token at update time) never loses data —
// crucially, the logout mutation does NOT touch this key.
const PENDING_MERGE_KEY = 'bk_pendingMerge';

// The single source of "are these the same line?" — must stay identical to the
// backend's line identity (itemId + variant name).
function sameLine(a, b) {
    return a._id === b._id && (a.variant?.name ?? null) === (b.variant?.name ?? null);
}

// Guests persist to localStorage; logged-in users don't — the server is the
// source of truth, and mirroring would double-count. Gate on the token.
function persist(state, key, arr) {
    if (!state.token) localStorage.setItem(key, JSON.stringify(arr));
}

// The API only cares about name + sku for a variant; everything else is
// ignored. Send null for a product with no variant selection.
function toApiVariant(variant) {
    if (!variant || !variant.name) return null;
    return { name: variant.name, sku: variant.sku ?? undefined };
}

// Server lines carry a thin variant ({ name, sku }) with no price. Resolve the
// full snapshot (price/salePrice/stock) from the product's variants array so
// the cart/wishlist pages can render prices. Falls back to minVariant.
function resolveVariant(product, variant) {
    const variants = product?.variants ?? [];
    const match = variant?.name
        ? variants.find(v => v.name === variant.name)
        : null;
    const full = match ?? product?.minVariant ?? variants[0] ?? {};
    return {
        name: full.name ?? variant?.name ?? null,
        sku: full.sku ?? variant?.sku ?? null,
        price: full.price ?? null,
        salePrice: full.salePrice ?? (product?.isOnSale ? product?.salePrice : null) ?? null,
        stock: full.stock ?? 0,
    };
}

// Map a nested server line ({ _id: lineId, product, quantity, variant }) into
// the flat display shape the cart/wishlist components already expect (_id is the
// PRODUCT id, not the line id).
function buildCartItem(line) {
    const p = line.product ?? {};
    return {
        _id: p._id,
        title: p.title,
        brand: p.brand,
        images: p.images,
        productCategory: p.productCategory,
        quantity: line.quantity ?? 1,
        variant: resolveVariant(p, line.variant),
    };
}

function buildWishlistItem(line) {
    const p = line.product ?? {};
    return {
        _id: p._id,
        title: p.title,
        brand: p.brand,
        images: p.images,
        productCategory: p.productCategory,
        minVariant: p.minVariant,
        variant: resolveVariant(p, line.variant),
    };
}

// Central API-error handling: expired/invalid token → log out + toast; other
// errors surface an optional message. Called after rollback in the actions.
function handleApiError(commit, error, message) {
    const reason = error?.response?.data?.reason;
    const status = error?.response?.status;
    if (reason === 'INVALID_TOKEN' || status === 403) {
        commit('logout');
        toastService.show({
            message: 'Your session expired. Please log in again.',
            status: 'error',
            duration: 3000,
        });
        return;
    }
    if (message) {
        toastService.show({ message, status: 'error', duration: 3000 });
    }
}

export const store = createStore({
    state: {
        user: JSON.parse(localStorage.getItem('bk_user')) || null,
        token: localStorage.getItem('bk_token') || null,
        cart: JSON.parse(localStorage.getItem('bk_cart')) || [],
        wishlist: JSON.parse(localStorage.getItem('bk_wishlist')) || [],
        // Product comparison: stores product IDs only (max 2). Full product
        // data is fetched on demand when the compare page mounts.
        compare: JSON.parse(localStorage.getItem('bk_compare')) || [],
        passwordResetNeeded: JSON.parse(localStorage.getItem('bk_passwordResetNeeded')) || false,
        // True while cart/wishlist are being hydrated from the server.
        isSyncing: false,
    },
    mutations: {
        setUser(state, user) {
            state.user = user;
        },
        setToken(state, token) {
            state.token = token;
        },
        setSyncing(state, value) {
            state.isSyncing = value;
        },
        setPasswordResetNeeded(state, passwordResetNeeded) {
            state.passwordResetNeeded = passwordResetNeeded;
        },
        logout(state) {
            state.user = null;
            state.token = null;
            // Clear cart & wishlist so the next guest on this device doesn't
            // inherit the previous user's items. The DB copy is untouched, so a
            // later login restores it.
            state.cart = [];
            state.wishlist = [];
            localStorage.removeItem('bk_token');
            localStorage.removeItem('bk_user');
            localStorage.removeItem('bk_passwordResetNeeded');
            localStorage.removeItem('bk_cart');
            localStorage.removeItem('bk_wishlist');
        },
        // Full-replace, used by hydrate AND rollback. persist() is a no-op when
        // logged in, so this never mirrors server state back to localStorage.
        setCart(state, cart) {
            state.cart = cart || [];
            persist(state, 'bk_cart', state.cart);
        },
        setWishlist(state, wishlist) {
            state.wishlist = wishlist || [];
            persist(state, 'bk_wishlist', state.wishlist);
        },
        addToCart(state, { product, variant, quantity = 1 }) {
            const cartItem = {
                _id: product._id,
                title: product.title,
                brand: product.brand,
                images: product.images,
                productCategory: product.productCategory,
                quantity: Number(quantity) || 1,

                // store full variant snapshot (sku is needed to target the
                // exact line on the API)
                variant: {
                    name: variant.name,
                    sku: variant.sku ?? null,
                    price: variant.price,
                    salePrice: variant.salePrice ?? null,
                    stock: variant.stock ?? 0,
                }
            };

            const existingItem = state.cart.find(item => sameLine(item, cartItem));

            if (existingItem) {
                existingItem.quantity += cartItem.quantity;
            } else {
                state.cart.push(cartItem);
            }

            persist(state, 'bk_cart', state.cart);
        },
        // Used when user updates quantity from cart page. Quantity is absolute.
        updateCartItemQuantity(state, payload) {
            const itemInCart = state.cart.find(item =>
                item._id === payload._id &&
                item.variant?.name === payload.variant?.name
            );

            if (itemInCart) {
                itemInCart.quantity = Number(payload.quantity) || 1;
            }

            persist(state, 'bk_cart', state.cart);
        },
        removeFromCart(state, payload) {
            state.cart = state.cart.filter(item =>
                !(
                    item._id === payload.productId &&
                    item.variant?.name === payload.variant?.name
                )
            );

            persist(state, 'bk_cart', state.cart);
        },
        clearCart(state) {
            state.cart = [];
            localStorage.removeItem('bk_cart');
        },
        addToWishlist(state, product) {
            const itemInWishlist = state.wishlist.find(item => sameLine(item, product));
            if (!itemInWishlist) {
                state.wishlist.push(product);
                persist(state, 'bk_wishlist', state.wishlist);
            }
        },
        removeFromWishlist(state, payload) {
            state.wishlist = state.wishlist.filter(item =>
                !(
                    item._id === payload.productId &&
                    item.variant?.name === payload.variant?.name
                )
            );
            persist(state, 'bk_wishlist', state.wishlist);
        },
        clearWishlist(state) {
            state.wishlist = [];
            localStorage.removeItem('bk_wishlist');
        },
        loadWishlistFromStorage(state) {
            const storedWishlist = JSON.parse(
                localStorage.getItem('bk_wishlist')
            );
            if (storedWishlist) {
                state.wishlist = storedWishlist;
            }
        },
        // Add a product id to the comparison list. Only stores ids, keeps them
        // unique, and caps the list at 2 products (left + right sides).
        addToCompare(state, productId) {
            if (!productId) return;
            if (state.compare.includes(productId)) return;
            if (state.compare.length >= 2) return;
            state.compare.push(productId);
            localStorage.setItem('bk_compare', JSON.stringify(state.compare));
        },
        removeFromCompare(state, productId) {
            state.compare = state.compare.filter((id) => id !== productId);
            localStorage.setItem('bk_compare', JSON.stringify(state.compare));
        },
        clearCompare(state) {
            state.compare = [];
            localStorage.removeItem('bk_compare');
        },
    },
    actions: {
        login({ commit, dispatch }, { user, token, passwordResetNeeded = false }) {
            commit('setUser', user);
            commit('setToken', token);
            commit('setPasswordResetNeeded', passwordResetNeeded);
            localStorage.setItem('bk_token', token);
            localStorage.setItem('bk_user', JSON.stringify(user));
            localStorage.setItem('bk_passwordResetNeeded', passwordResetNeeded);
            // Merge any guest cart/wishlist into the account, then hydrate.
            dispatch('mergeGuestData');
        },
        logout({ commit }) {
            commit('logout');
        },
        checkLogin({ commit }) {
            const token = localStorage.getItem('bk_token');
            const user = JSON.parse(localStorage.getItem('bk_user'));
            if (token && user) {
                commit('setUser', user);
                commit('setToken', token);
            }
        },

        // On login: sum guest cart quantities into the server cart, union the
        // wishlist, clear localStorage, then load canonical state from server.
        async mergeGuestData({ state, dispatch }) {
            if (!state.token) return;

            // Source data: a prior interrupted attempt (survives logout) takes
            // priority over the current local guest data.
            const pending = JSON.parse(localStorage.getItem(PENDING_MERGE_KEY) || 'null');
            const localCart = pending?.cart ?? clone(state.cart);
            const localWishlist = pending?.wishlist ?? clone(state.wishlist);

            if (!localCart.length && !localWishlist.length) {
                localStorage.removeItem(PENDING_MERGE_KEY);
                await dispatch('hydrateFromServer');
                return;
            }

            // Preflight: confirm the token actually works before we clear
            // anything. On auth/network failure, stash the guest data under a key
            // logout won't wipe and bail — it retries on the next successful
            // login. We deliberately do NOT surface a session-expired toast/logout
            // here; that would wipe the live copy before the backup is trusted.
            let serverItems = [];
            try {
                const res = await cartApi.get(state.token);
                serverItems = (res.data?.cart ?? []).map(buildCartItem);
            } catch {
                localStorage.setItem(
                    PENDING_MERGE_KEY,
                    JSON.stringify({ cart: localCart, wishlist: localWishlist })
                );
                return;
            }

            // From here the token is known-good, so writes only fail per-item
            // (e.g. a deleted product); those are safe to skip.
            for (const it of localCart) {
                // Cart: absolute quantity = local + whatever the server already has.
                const match = serverItems.find(s => sameLine(s, it));
                const newQty = (it.quantity ?? 1) + (match?.quantity ?? 0);
                try {
                    await cartApi.upsert(
                        { productId: it._id, quantity: newQty, variant: toApiVariant(it.variant) },
                        state.token
                    );
                } catch { /* skip this line; hydrate reflects what actually landed */ }
            }

            // Wishlist: union (server dedupes and returns alreadyExists: true).
            for (const it of localWishlist) {
                try {
                    await wishlistApi.add(
                        { productId: it._id, variant: toApiVariant(it.variant) },
                        state.token
                    );
                } catch { /* ignore; hydrate reflects the canonical set */ }
            }

            localStorage.removeItem('bk_cart');
            localStorage.removeItem('bk_wishlist');
            localStorage.removeItem(PENDING_MERGE_KEY);
            await dispatch('hydrateFromServer');
        },

        // On boot / after merge: replace local state with canonical server state.
        async hydrateFromServer({ state, commit }) {
            if (!state.token) return;
            commit('setSyncing', true);
            try {
                const [c, w] = await Promise.allSettled([
                    cartApi.get(state.token),
                    wishlistApi.get(state.token),
                ]);
                const cart = c.status === 'fulfilled' ? (c.value.data?.cart ?? []) : [];
                const wish = w.status === 'fulfilled' ? (w.value.data?.wishlist ?? []) : [];
                commit('setCart', cart.map(buildCartItem).filter(i => i._id));
                commit('setWishlist', wish.map(buildWishlistItem).filter(i => i._id));
                const failed = [c, w].find(r => r.status === 'rejected');
                if (failed) handleApiError(commit, failed.reason, null);
            } finally {
                commit('setSyncing', false);
            }
        },

        // Write pattern: snapshot → optimistic commit → (guest? stop) → API →
        // rollback + toast on failure.
        async addToCart({ state, commit }, payload) {
            const snapshot = state.token ? clone(state.cart) : null;
            commit('addToCart', payload);
            if (!state.token) return;
            // Absolute quantity AFTER the local merge, and the normalized variant.
            const line = state.cart.find(i =>
                i._id === payload.product._id &&
                i.variant?.name === payload.variant?.name
            );
            if (!line) return;
            try {
                await cartApi.upsert(
                    { productId: line._id, quantity: line.quantity, variant: toApiVariant(line.variant) },
                    state.token
                );
            } catch (e) {
                commit('setCart', snapshot);
                handleApiError(commit, e, 'Could not add to cart.');
            }
        },

        async updateCartItemQuantity({ state, commit }, payload) {
            const snapshot = state.token ? clone(state.cart) : null;
            commit('updateCartItemQuantity', payload);
            if (!state.token) return;
            const line = state.cart.find(i =>
                i._id === payload._id &&
                i.variant?.name === payload.variant?.name
            );
            if (!line) return;
            try {
                await cartApi.upsert(
                    { productId: line._id, quantity: line.quantity, variant: toApiVariant(line.variant) },
                    state.token
                );
            } catch (e) {
                commit('setCart', snapshot);
                handleApiError(commit, e, 'Could not update quantity.');
            }
        },

        async removeFromCart({ state, commit }, payload) {
            const snapshot = state.token ? clone(state.cart) : null;
            commit('removeFromCart', payload);
            if (!state.token) return;
            try {
                await cartApi.remove(
                    { productId: payload.productId, variant: toApiVariant(payload.variant) },
                    state.token
                );
            } catch (e) {
                commit('setCart', snapshot);
                handleApiError(commit, e, 'Could not remove item.');
            }
        },

        clearCart({ commit }) {
            commit('clearCart');
        },

        async addToWishlist({ state, commit }, item) {
            const snapshot = state.token ? clone(state.wishlist) : null;
            const already = state.wishlist.some(i => sameLine(i, item));
            commit('addToWishlist', item);
            if (!state.token || already) return;
            try {
                // Server dedupes and returns 200 { alreadyExists: true } — no
                // error to special-case.
                await wishlistApi.add(
                    { productId: item._id, variant: toApiVariant(item.variant) },
                    state.token
                );
            } catch (e) {
                commit('setWishlist', snapshot);
                handleApiError(commit, e, 'Could not add to wishlist.');
            }
        },

        // Accepts the full item (so the variant reaches the API); also tolerates
        // a bare id for backward compatibility.
        async removeFromWishlist({ state, commit }, item) {
            const productId = typeof item === 'string' ? item : item._id;
            const variant = typeof item === 'string' ? undefined : item.variant;
            const snapshot = state.token ? clone(state.wishlist) : null;
            commit('removeFromWishlist', { productId, variant });
            if (!state.token) return;
            try {
                await wishlistApi.remove(
                    { productId, variant: toApiVariant(variant) },
                    state.token
                );
            } catch (e) {
                commit('setWishlist', snapshot);
                handleApiError(commit, e, 'Could not remove from wishlist.');
            }
        },

        clearWishlist({ commit }) {
            commit('clearWishlist');
        },
        loadWishlistFromStorage({ commit }) {
            commit('loadWishlistFromStorage');
        },
        addToCompare({ commit }, productId) {
            commit('addToCompare', productId);
        },
        removeFromCompare({ commit }, productId) {
            commit('removeFromCompare', productId);
        },
        clearCompare({ commit }) {
            commit('clearCompare');
        },
    },
    getters: {
        token: (state) => state.token,
        isLoggedIn: (state) => !!state.user,
        isSyncing: (state) => state.isSyncing,
        cartItems: (state) => state.cart,
        cartItemCount: (state) => state.cart.length,
        cartTotalPrice: (state) => {
            return state.cart.reduce((total, item) => {
                return total + item.price * item.quantity;
            }, 0);
        },
        cartItemTotalCount: (state) => {
            return state.cart.reduce((count, item) => {
                return count + item.quantity;
            }, 0);
        },
        wishlistItems: (state) => state.wishlist,
        wishlistItemCount: (state) => state.wishlist.length,
        compareItems: (state) => state.compare,
        compareItemCount: (state) => state.compare.length,
    },
});
